import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Box,
  Button,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { ErrorState } from './ErrorState';
import { SkeletonList } from './LoadingState';

export interface CatalogRow {
  id: string;
  title: string;
  subtitle?: React.ReactNode;
  extra?: React.ReactNode;
  href?: string;
  muted?: boolean;
}

interface CatalogListProps {
  title: string;
  icon?: React.ReactNode;
  rows: CatalogRow[];
  isLoading: boolean;
  error?: unknown;
  onRetry?: () => void;
  totalPages?: number;
  /** Путь формы создания; кнопка видна, только если передан */
  createHref?: string;
  createLabel?: string;
  emptyText?: string;
}

/** Хук параметров списка в URL: `?q=&page=`. */
export const useCatalogParams = () => {
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page') ?? '1') || 1;
  return { q: q || undefined, page };
};

/** Список каталога: поиск в URL, строки открывают карточку, «Создать» — только в режиме «Админ». */
export const CatalogList: React.FC<CatalogListProps> = ({
  title,
  icon,
  rows,
  isLoading,
  error,
  onRetry,
  totalPages = 1,
  createHref,
  createLabel = 'Создать',
  emptyText = 'Пока пусто',
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page') ?? '1') || 1;
  const [input, setInput] = useState(q);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const value = input.trim();
      if (value === q) return;
      const next = new URLSearchParams(searchParams);
      if (value) next.set('q', value);
      else next.delete('q');
      next.delete('page');
      setSearchParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [input, q, searchParams, setSearchParams]);

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        {icon}
        <Typography variant="h2" component="h1" sx={{ flex: 1 }}>
          {title}
        </Typography>
        {createHref && (
          <Button variant="contained" color="manage" startIcon={<Add />} onClick={() => navigate(createHref)} sx={{ minHeight: 40 }}>
            {createLabel}
          </Button>
        )}
      </Box>
      <TextField
        fullWidth
        type="search"
        size="small"
        label="Поиск"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        slotProps={{
          htmlInput: { enterKeyHint: 'search', autoComplete: 'off' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 1 }}
      />
      {isLoading ? (
        <SkeletonList count={6} />
      ) : error ? (
        <ErrorState message="Ошибка загрузки" onRetry={onRetry} />
      ) : rows.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{q ? 'Ничего не найдено' : emptyText}</Typography>
          {createHref && !q && (
            <Button sx={{ mt: 2 }} variant="outlined" color="manage" startIcon={<Add />} onClick={() => navigate(createHref)}>
              {createLabel}
            </Button>
          )}
        </Box>
      ) : (
        <List disablePadding>
          {rows.map((row) => (
            <ListItemButton
              key={row.id}
              disabled={!row.href}
              onClick={() => row.href && navigate(row.href)}
              sx={{ minHeight: 56, borderBottom: 1, borderColor: 'divider', opacity: row.muted ? 0.6 : 1, '&.Mui-disabled': { opacity: row.muted ? 0.6 : 1 } }}
            >
              <ListItemText primary={row.title} secondary={row.subtitle} slotProps={{ secondary: { component: 'div' } }} />
              {row.extra}
            </ListItemButton>
          ))}
        </List>
      )}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Box>
  );
};
